import { useEffect, useRef, useState } from "react";
import { Link, NavLink } from "react-router";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBars,
  faGlobe,
  faRobot,
  faTimes,
  faUser
} from "@fortawesome/free-solid-svg-icons";
import { clearSession, readSessionProfile } from "../services/authApi";

const NAV_LINKS = [
  { label: "Home", href: "/", external: false },
  { label: "Buy", href: "https://buyrentsale.com/w/buy", external: true },
  { label: "Rent", href: "https://buyrentsale.com/w/rent", external: true },
  { label: "Invest", href: "https://hyderabadlandinvestments.com/", external: true },
  { label: "Projects", href: "https://buyrentsale.com/w/viewprojects", external: true },
  { label: "Blog", href: "/blog" },
  { label: "About Us", href: "/about" },
  { label: "How it Works", href: "/how-it-works" },
  { label: "Contact Us", href: "/contact" }
];

const resolvePhotoUrl = (photoUrl) => {
  if (!photoUrl) {
    return "";
  }
  if (/^https?:\/\//i.test(photoUrl)) {
    return photoUrl;
  }
  return `https://api.buyrentsale.com/${photoUrl.replace(/^\//, "")}`;
};

function NavItem({ link, onNavigate, className = "vd-navlink" }) {
  if (link.external) {
    return <a
      href={link.href}
      target="_blank"
      rel="noopener noreferrer"
      className={className}
      onClick={onNavigate}
    >
      {link.label}
    </a>;
  }

  return <NavLink
    to={link.href}
    end={link.href === "/"}
    className={({ isActive }) => `${className}${isActive ? " is-active" : ""}`}
    onClick={onNavigate}
  >
    {link.label}
  </NavLink>;
}

function Navbar({ onAdvisorClick }) {
  const [open, setOpen] = useState(false);
  const [profile, setProfile] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [photoFailed, setPhotoFailed] = useState(false);
  const profileRef = useRef(null);
  const photoUrl = resolvePhotoUrl(profile?.photoUrl);

  useEffect(() => {
    const syncProfile = () => {
      setProfile(readSessionProfile());
      setPhotoFailed(false);
    };

    syncProfile();
    window.addEventListener("localStorageChange", syncProfile);
    window.addEventListener("storage", syncProfile);
    return () => {
      window.removeEventListener("localStorageChange", syncProfile);
      window.removeEventListener("storage", syncProfile);
    };
  }, []);

  useEffect(() => {
    if (!menuOpen) {
      return undefined;
    }

    const closeMenu = (event) => {
      if (!profileRef.current?.contains(event.target)) {
        setMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", closeMenu);
    return () => document.removeEventListener("mousedown", closeMenu);
  }, [menuOpen]);

  const logout = () => {
    clearSession();
    setMenuOpen(false);
    window.dispatchEvent(new Event("localStorageChange"));
  };

  return <>
      <nav className="vd-navbar">
        <Link to="/" className="vd-logo-wrap" style={{ textDecoration: "none" }} onClick={() => setOpen(false)}>
          <div className="vd-logo-text">
            Ventures<span className="blue">Directory</span>
            <span className="vd-logo-ai-badge">AI</span>
          </div>
          <div className="vd-logo-sub">AI Real Estate Discovery</div>
        </Link>

        <div className="vd-nav-links">
          {NAV_LINKS.map((link) => <NavItem key={link.label} link={link} />)}
        </div>

        <div className="vd-nav-actions">
          <a
            href="#investmentModal"
            className="vd-btn-advisor"
            onClick={(event) => {
              event.preventDefault();
              onAdvisorClick();
            }}
          >
            <FontAwesomeIcon icon={faRobot} />
            AI Advisor
          </a>

          {profile ? <div className="vd-profile" ref={profileRef}>
            <button
              type="button"
              className="vd-profile-btn"
              aria-label={`Profile, ${profile.displayName}`}
              aria-haspopup="menu"
              aria-expanded={menuOpen}
              onClick={() => setMenuOpen((current) => !current)}
            >
              {photoUrl && !photoFailed
                ? <img src={photoUrl} alt="" onError={() => setPhotoFailed(true)} />
                : <FontAwesomeIcon icon={faUser} />}
            </button>
            {menuOpen ? <div className="vd-profile-menu" role="menu">
              <div className="vd-profile-name">{profile.displayName}</div>
              {profile.displayMobile && profile.displayMobile !== profile.displayName
                ? <div className="vd-profile-mobile">{profile.displayMobile}</div>
                : null}
              <button type="button" role="menuitem" onClick={logout}>Logout</button>
            </div> : null}
          </div> : null}

          <button
            className="vd-icon-btn d-lg-none"
            onClick={() => setOpen(!open)}
            style={{ borderRadius: 6 }}
            aria-label={open ? "Close menu" : "Open menu"}
          >
            <FontAwesomeIcon icon={open ? faTimes : faBars} />
          </button>
        </div>
      </nav>

      {open && <div className="vd-mobile-menu">
          <div className="d-flex flex-column gap-1">
            {NAV_LINKS.map((link) => <NavItem
              key={link.label}
              link={link}
              className="vd-navlink"
              onNavigate={() => setOpen(false)}
            />)}
            <hr style={{ margin: "8px 0", borderColor: "#e5e7eb" }} />
            <a href="#" className="vd-navlink" style={{ fontSize: 14 }}>
              <FontAwesomeIcon icon={faGlobe} style={{ width: 14 }} />
              India (IN)
            </a>
            {profile ? <button type="button" className="vd-navlink" style={{ fontSize: 14, border: 0, background: "transparent", textAlign: "left" }} onClick={logout}>
              Logout
            </button> : <a href="#" className="vd-navlink" style={{ fontSize: 14 }}>
              Login / Signup
            </a>}
          </div>
        </div>}
    </>;
}

export default Navbar;
